import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell, LayoutGrid, List, PencilRuler, Search, Trash2 } from "lucide-react";

type CatalogMeditation = {
  id: string;
  title: string;
  description: string;
  durationMinutes: number;
  level: "beginner" | "intermediate" | "advanced";
  coverImageUrl?: string;
  audioUrl?: string;
  active: boolean;
  tags: string[];
  content_language?: string;
  translation_group_id?: string;
};

type MeditationsResponse = {
  ok: true;
  items: CatalogMeditation[];
};

type ContentMode = "meditation" | "breath";

function normalizeTag(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function ensureBreathTag(tags: string[]) {
  const hasNefes = tags.some((tag) => normalizeTag(tag) === "nefes");
  return hasNefes ? tags : ["nefes", ...tags];
}

function toPublicUrl(rawValue?: string) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:") || raw.startsWith("/")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("ftp://")) return raw.replace(/^ftp:\/\//i, "https://");
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return `https://${raw}`;
}

function formatDuration(durationMinutes: number) {
  const totalSeconds = Math.max(0, Math.round(Math.max(0, Number(durationMinutes) || 0) * 60));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return secs === 0 ? `${mins} dk` : `${mins}:${String(secs).padStart(2, "0")}`;
}

function getMeditationApiBase(mode: ContentMode) {
  return mode === "breath" ? "/api/admin/numeroloji/breaths" : "/api/admin/numeroloji/meditations";
}

async function fetchMeditations(mode: ContentMode): Promise<CatalogMeditation[]> {
  const res = await adminFetch(getMeditationApiBase(mode));
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansları alınamadı." : "Meditasyonlar alınamadı.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  const data = json as MeditationsResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

async function saveMeditation(mode: ContentMode, payload: Partial<CatalogMeditation> & { title: string }) {
  const res = await adminFetch(getMeditationApiBase(mode), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansı kaydedilemedi." : "Meditasyon kaydedilemedi.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  return json as { ok: true; item: CatalogMeditation };
}

async function deleteMeditation(mode: ContentMode, id: string) {
  const res = await adminFetch(`${getMeditationApiBase(mode)}/${encodeURIComponent(id)}/delete`, {
    method: "POST",
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    const fallback = mode === "breath" ? "Nefes seansı silinemedi." : "Meditasyon silinemedi.";
    throw new Error(typeof json?.error === "string" ? json.error : fallback);
  }
  return json as { ok: true; deleted: boolean };
}

export default function AdminNumerolojiMeditations({ mode = "meditation" }: { mode?: ContentMode } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const isBreathMode = mode === "breath";
  const isPersistedStorage = sessionQuery.data?.numerolojiStorage === "supabase";
  const listTitle = isBreathMode ? "Nefes Yönetimi" : "Meditasyon Yönetimi";
  const collectionTitle = isBreathMode ? "Nefes Seansları" : "Meditasyonlar";
  const descriptionText = isBreathMode
    ? "Nefes seanslarını yönetebilir ve kullanıcı panelinde yayınlayabilirsiniz."
    : "Etiketleyerek kişiye özel meditasyonlar sunabilirsiniz.";
  const basePath = isBreathMode ? "/admin/numeroloji/nefes" : "/admin/numeroloji/meditasyon";

  const meditationsQuery = useQuery({
    queryKey: ["admin-numeroloji-meditations", mode],
    queryFn: () => fetchMeditations(mode),
    enabled: authenticated,
    staleTime: 0,
  });

  const meditations = useMemo(() => {
    const rows = meditationsQuery.data ?? [];
    return rows;
  }, [meditationsQuery.data]);

  const meditationSaveMutation = useMutation({
    mutationFn: (payload: any) => saveMeditation(mode, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-meditations", mode] });
    },
  });

  const meditationDeleteMutation = useMutation({
    mutationFn: (id: string) => deleteMeditation(mode, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-meditations", mode] });
    },
  });

  const [meditationSearch, setMeditationSearch] = useState("");
  const [meditationFilter, setMeditationFilter] = useState<"all" | "active" | "inactive">("all");
  const [meditationSort, setMeditationSort] = useState<"newest" | "title-asc" | "duration-asc" | "duration-desc">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [langTab, setLangTab] = useState<"all" | "tr" | "en" | "es">("all");

  const langCounts = useMemo(() => {
    const tr = meditations.filter((m) => (m.content_language ?? 'tr') === 'tr').length;
    const en = meditations.filter((m) => m.content_language === 'en').length;
    const es = meditations.filter((m) => m.content_language === 'es').length;
    return { tr, en, es };
  }, [meditations]);

  const visibleMeditations = useMemo(() => {
    const q = meditationSearch.trim().toLowerCase();

    let rows = meditations;

    if (langTab !== 'all') {
      rows = rows.filter((m) => (m.content_language ?? 'tr') === langTab);
    }

    if (q) {
      rows = rows.filter((m) => {
        const hay = `${m.title} ${m.description} ${m.level} ${(m.tags ?? []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (meditationFilter === "active") {
      rows = rows.filter((m) => m.active);
    } else if (meditationFilter === "inactive") {
      rows = rows.filter((m) => !m.active);
    }

    const sorted = [...rows];
    if (meditationSort === "title-asc") {
      sorted.sort((a, b) => String(a.title || "").localeCompare(String(b.title || ""), "tr"));
    } else if (meditationSort === "duration-asc") {
      sorted.sort((a, b) => Number(a.durationMinutes || 0) - Number(b.durationMinutes || 0));
    } else if (meditationSort === "duration-desc") {
      sorted.sort((a, b) => Number(b.durationMinutes || 0) - Number(a.durationMinutes || 0));
    }
    // "newest" = server order (created_at DESC from hydration)

    return sorted;
  }, [langTab, meditationFilter, meditationSearch, meditationSort, meditations]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {listTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        {sessionQuery.data?.numerolojiStorage === "memory" ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100 sm:px-6">
            Uyarı: Veri tabanı bağlantısı sunucuda aktif değil. Meditasyon/Nefes kayıtları kalıcı kaydedilmez ve sunucu
            yenilenince kaybolabilir. (Sunucuda SUPABASE_URL + SUPABASE_SERVICE_ROLE_KEY gerekli.)
          </div>
        ) : null}

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {meditationsQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : meditationsQuery.isError ? (
            <div className="text-sm text-destructive">{(meditationsQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{collectionTitle}</div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">{descriptionText}</div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                      <div className="text-xl font-extrabold">{meditations.filter((m) => m.active).length}</div>
                    </div>
                  </div>

                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-slate-500/10 text-slate-600 dark:text-slate-300">
                      <div className="h-2 w-2 rounded-full bg-slate-400" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Pasif</div>
                      <div className="text-xl font-extrabold">{meditations.filter((m) => !m.active).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {([
                  { key: 'all', label: 'Tüm Diller' },
                  { key: 'tr', label: '🇹🇷 Türkçe', count: langCounts.tr },
                  { key: 'en', label: '🇬🇧 İngilizce', count: langCounts.en },
                  { key: 'es', label: '🇪🇸 İspanyolca', count: langCounts.es },
                ] as const).map(({ key, label, count }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setLangTab(key)}
                    className={cn(
                      "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                      langTab === key
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
                    )}
                  >
                    {label}{count != null ? ` (${count})` : ''}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Başlık, seviye, etiket..."
                    value={meditationSearch}
                    onChange={(e) => setMeditationSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={meditationFilter === "all" ? "default" : "outline"}
                    className={cn("rounded-full", meditationFilter !== "all" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setMeditationFilter("all")}
                  >
                    Tümü
                  </Button>
                  <Button
                    size="sm"
                    variant={meditationFilter === "active" ? "default" : "outline"}
                    className={cn("rounded-full", meditationFilter !== "active" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setMeditationFilter("active")}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="sm"
                    variant={meditationFilter === "inactive" ? "default" : "outline"}
                    className={cn("rounded-full", meditationFilter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setMeditationFilter("inactive")}
                  >
                    Pasif
                  </Button>

                  <div className="ml-0 flex items-center gap-2 lg:ml-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={meditationSort}
                      onChange={(e) => setMeditationSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Başlık (A-Z)</option>
                      <option value="duration-asc">Süre (Artan)</option>
                      <option value="duration-desc">Süre (Azalan)</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-950/40">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  <Button
                    size="sm"
                    className="rounded-full"
                    onClick={() => navigate(`${basePath}/yeni`)}
                    disabled={!isPersistedStorage}
                  >
                    <span className="mr-1">+</span>
                    {isBreathMode ? "Yeni nefes seansı" : "Yeni meditasyon"}
                  </Button>
                </div>
              </div>

              {meditationSaveMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(meditationSaveMutation.error as Error).message}
                </div>
              ) : null}

              {meditationDeleteMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(meditationDeleteMutation.error as Error).message}
                </div>
              ) : null}

              {viewMode === "grid" ? (
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visibleMeditations.map((m) => {
                    const badge = m.active ? "Aktif" : "Pasif";
                    const badgeClass = m.active ? "bg-emerald-500" : "bg-slate-500";
                    const coverImageUrl = toPublicUrl(m.coverImageUrl);

                    return (
                      <div
                        key={m.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div
                          className="relative aspect-[4/3] bg-gradient-to-br from-indigo-600/25 via-blue-600/10 to-slate-950/20"
                          style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                        >
                          <div
                            className={cn(
                              "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                              badgeClass,
                            )}
                          >
                            {badge}
                          </div>

                          <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() => navigate(`${basePath}/${encodeURIComponent(m.id)}`)}
                              disabled={!isPersistedStorage}
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={meditationDeleteMutation.isPending || !isPersistedStorage}
                              onClick={() => {
                                const ok = confirm(isBreathMode ? "Bu nefes seansını silmek istiyor musunuz?" : "Bu meditasyonu silmek istiyor musunuz?");
                                if (!ok) return;
                                meditationDeleteMutation.mutate(m.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 left-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                            {formatDuration(m.durationMinutes)} • {m.level}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 text-base font-bold text-slate-900 dark:text-white">{m.title}</div>
                            {m.content_language && m.content_language !== 'tr' && (
                              <span className="mt-0.5 shrink-0 text-sm">{m.content_language === 'en' ? '🇬🇧' : '🇪🇸'}</span>
                            )}
                          </div>
                          <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{m.description}</div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-1">
                              {(m.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span
                                  key={`${m.id}-tag-${idx}`}
                                  className="rounded-full bg-blue-600/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={m.active}
                                disabled={meditationSaveMutation.isPending || !isPersistedStorage}
                                onChange={() =>
                                  meditationSaveMutation.mutate({
                                    ...m,
                                    tags: isBreathMode ? ensureBreathTag(m.tags ?? []) : m.tags,
                                    active: !m.active,
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate(`${basePath}/yeni`)}
                    disabled={!isPersistedStorage}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">{isBreathMode ? "Yeni nefes seansı ekle" : "Yeni meditasyon ekle"}</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      {isBreathMode ? "Yeni bir nefes çalışması oluştur." : "Yeni bir meditasyon seansı oluştur."}
                    </div>
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visibleMeditations.map((m) => {
                    const badge = m.active ? "Aktif" : "Pasif";
                    const badgeClass = m.active ? "bg-emerald-500" : "bg-slate-500";
                    const coverImageUrl = toPublicUrl(m.coverImageUrl);

                    return (
                      <div
                        key={m.id}
                        className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div
                          className="relative w-24 flex-none bg-gradient-to-br from-indigo-600/25 via-blue-600/10 to-slate-950/20 sm:w-36"
                          style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                        >
                          <div className={cn("absolute left-2 top-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white", badgeClass)}>
                            {badge}
                          </div>
                        </div>

                        <div className="flex flex-1 items-center gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <div className="truncate text-sm font-bold text-slate-900 dark:text-white">{m.title}</div>
                              {m.content_language && m.content_language !== 'tr' && (
                                <span className="shrink-0 text-xs">{m.content_language === 'en' ? '🇬🇧' : '🇪🇸'}</span>
                              )}
                            </div>
                            <div className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{m.description}</div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {(m.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span key={`${m.id}-tag-${idx}`} className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="hidden flex-none sm:block">
                            <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              {formatDuration(m.durationMinutes)} • {m.level}
                            </div>
                          </div>

                          <div className="flex flex-none items-center gap-2">
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                              onClick={() => navigate(`${basePath}/${encodeURIComponent(m.id)}`)}
                              disabled={!isPersistedStorage}
                            >
                              <PencilRuler className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-800 dark:hover:bg-rose-900/20"
                              disabled={meditationDeleteMutation.isPending || !isPersistedStorage}
                              onClick={() => {
                                const ok = confirm(isBreathMode ? "Bu nefes seansını silmek istiyor musunuz?" : "Bu meditasyonu silmek istiyor musunuz?");
                                if (!ok) return;
                                meditationDeleteMutation.mutate(m.id);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={m.active}
                                disabled={meditationSaveMutation.isPending || !isPersistedStorage}
                                onChange={() =>
                                  meditationSaveMutation.mutate({
                                    ...m,
                                    tags: isBreathMode ? ensureBreathTag(m.tags ?? []) : m.tags,
                                    active: !m.active,
                                  })
                                }
                              />
                              <div className="h-5 w-9 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-[16px] dark:bg-slate-700" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate(`${basePath}/yeni`)}
                    disabled={!isPersistedStorage}
                    className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                  >
                    <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-xl">+</span>
                    </div>
                    <div className="text-sm font-bold">{isBreathMode ? "Yeni nefes seansı ekle" : "Yeni meditasyon ekle"}</div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
